/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  Box,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import BadgeIcon from "@mui/icons-material/Badge";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Grid from "@mui/material/Grid2";
import useFormData from "@/hooks/useFormData";
import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";

const CertificateLoader = ({
  open,
  onClose,
  userId,
  setToast,
  onCertificateLoad,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    expires: false,
    expires_at: "",
  });
  const [errors, setErrors] = useState({});
  const [fileNames, setFileNames] = useState([]);
  const { postData } = useFormData();

  const steps = [
    {
      label: t("certificateLoader.stepCertificateDetails"),
      icon: <BadgeIcon />,
    },
    { label: t("certificateLoader.stepFileUpload"), icon: <CloudUploadIcon /> },
  ];

  const [activeStep, setActiveStep] = useState(0);

  const validateField = (name, value, expires = formData.expires) => {
    let error = "";
    if (!value.trim()) {
      if (name === "title" || name === "description") {
        error = t("certificateLoader.requiredError");
      }
      if (name === "expires_at" && expires) {
        error = t("certificateLoader.expiresAtRequiredError");
      }
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Update form data
    setFormData((prev) => {
      const updatedForm = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // If expires checkbox is changed, validate expires_at field
      if (name === "expires") {
        validateField("expires_at", prev.expires_at, checked);

        // Clear expires_at if expires is unchecked
        if (!checked) {
          updatedForm.expires_at = "";
        }
      } else {
        validateField(name, type === "checkbox" ? checked.toString() : value);
      }

      return updatedForm;
    });
  };

  const handleBlur = (e) => {
    validateField(e.target.name, e.target.value);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setFileNames(files.map((file) => file.name));
  };

  const isFormValid = () => {
    const isTitleValid = formData.title.trim() !== "";
    const isDescriptionValid = formData.description.trim() !== "";
    const isExpiryValid =
      !formData.expires ||
      (formData.expires && formData.expires_at.trim() !== "");

    return isTitleValid && isDescriptionValid && isExpiryValid;
  };

  const isFirstStepValid = () => {
    const isTitleValid = formData.title.trim() !== "";
    const isDescriptionValid = formData.description.trim() !== "";
    const isExpiryValid =
      !formData.expires ||
      (formData.expires && formData.expires_at.trim() !== "");

    return isTitleValid && isDescriptionValid && isExpiryValid;
  };

  const convertToUTC = (localDateTime) => {
    if (!localDateTime) return "";
    const date = new Date(localDateTime);
    return date.toISOString(); // Converts to UTC in ISO 8601 format
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Only proceed if first step is valid
      if (isFirstStepValid()) {
        setActiveStep(1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    const form = new FormData();
    const sentData = {
      user_id: userId,
      title: formData.title,
      description: formData.description,
      expires: formData.expires,
      expires_at: formData.expires ? convertToUTC(formData.expires_at) : null,
    };

    form.append(
      "data",
      new Blob([JSON.stringify(sentData)], { type: "application/json" })
    );

    // Only append attachments if files were selected
    if (selectedFiles.length > 0) {
      selectedFiles.forEach((file) => {
        form.append("attachments", file);
      });
    }

    const url = `/api/v1/users/${userId}/certificates`;
    try {
      await postData(form, url);
      setToast({
        open: true,
        severity: "success",
        message: t("certificateLoader.successToast"),
      });
      onCertificateLoad();
      onClose();
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: t("certificateLoader.errorToast", { error: error.message }),
      });
    }
    onCertificateLoad();
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            {/* Certificate Name */}
            <Grid size={12}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                  background: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <BadgeIcon color="primary" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    {t("certificateLoader.certificateInformation")}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label={t("certificateLoader.titleLabel")}
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  error={!!errors.title}
                  helperText={errors.title}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    },
                  }}
                  aria-label={t("certificateLoader.titleAria")}
                />
                <TextField
                  fullWidth
                  label={t("certificateLoader.descriptionLabel")}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    },
                  }}
                  aria-label={t("certificateLoader.descriptionAria")}
                />
              </Paper>
            </Grid>

            {/* Expiration Settings */}
            <Grid size={12}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  background: formData.expires
                    ? alpha(theme.palette.warning.main, 0.02)
                    : alpha(theme.palette.grey[50], 0.5),
                  ...(theme.palette.mode === "dark" &&
                    !formData.expires && {
                      background: alpha(theme.palette.grey[800], 0.5),
                    }),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <EventIcon color={formData.expires ? "warning" : "action"} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    {t("certificateLoader.expirationSettings")}
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.expires}
                      onChange={handleChange}
                      name="expires"
                      color="warning"
                      aria-label={t("certificateLoader.expiresAria")}
                    />
                  }
                  label={t("certificateLoader.expiresLabel")}
                  sx={{ mb: formData.expires ? 2 : 0 }}
                />

                {formData.expires && (
                  <TextField
                    fullWidth
                    label={t("certificateLoader.expiresAtLabel")}
                    name="expires_at"
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={formData.expires}
                    error={!!errors.expires_at}
                    helperText={errors.expires_at}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "background.paper",
                      },
                    }}
                    aria-label={t("certificateLoader.expiresAtAria")}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.1),
                  background: alpha(theme.palette.info.main, 0.02),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <CloudUploadIcon color="info" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    {t("certificateLoader.uploadCertificateFile")}
                  </Typography>
                </Box>

                <FileUploadField
                  label={t("certificateLoader.fileUploadLabel")}
                  fileNames={fileNames}
                  onChange={handleFileChange}
                  multiple={false}
                  inputId="file-upload"
                />

                {selectedFiles.length > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      border: "1px solid",
                      borderColor: alpha(theme.palette.success.main, 0.2),
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "success.dark",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 16 }} />
                      {t("certificateLoader.fileSelected", {
                        filename: selectedFiles[0]?.name,
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      {t("certificateLoader.fileSize", {
                        size: (selectedFiles[0]?.size / 1024 / 1024).toFixed(2),
                      })}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Summary */}
            <Grid size={12}>
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.2),
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("certificateLoader.certificateSummary")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t("certificateLoader.summaryName")}:</strong>{" "}
                  {formData.title || t("certificateLoader.notSpecified")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t("certificateLoader.summaryDescription")}:</strong>{" "}
                  {formData.description || t("certificateLoader.notSpecified")}
                </Typography>
                <Typography variant="body2">
                  <strong>{t("certificateLoader.summaryExpires")}:</strong>{" "}
                  {formData.expires
                    ? t("certificateLoader.yesExpires", {
                        date: formData.expires_at,
                      })
                    : t("certificateLoader.noExpires")}
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          boxShadow: theme.shadows[24],
          border: "1px solid",
          borderColor: "divider",
          background: theme.palette.background.paper,
          overflow: "hidden",
        },
      }}
      aria-labelledby="certificate-loader-dialog-title"
    >
      <DialogTitle
        id="certificate-loader-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 3,
          px: 4,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BadgeIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {t("certificateLoader.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.5,
              }}
            >
              {t("certificateLoader.addCertificateSubtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            "&:hover": {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
          aria-label={t("certificateLoader.closeDialogAria")}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  icon={step.icon}
                  sx={{
                    "& .MuiStepLabel-iconContainer": {
                      color:
                        index <= activeStep ? "primary.main" : "text.disabled",
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions
        sx={{
          p: 4,
          pt: 0,
          gap: 2,
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("certificateLoader.cancelButton")}
        </Button>

        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("certificateLoader.backButton")}
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!isFirstStepValid()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                },
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.action.disabled, 0.12),
                },
              }}
            >
              {t("certificateLoader.nextButton")}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isFormValid()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: theme.shadows[2],
                "&:hover": {
                  boxShadow: theme.shadows[4],
                },
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.action.disabled, 0.12),
                },
              }}
              aria-label={t("certificateLoader.submitButtonAria")}
            >
              {t("certificateLoader.submitButton")}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CertificateLoader;
